import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FaGithub, FaCodeBranch, FaStar, FaExclamationCircle, FaSync } from 'react-icons/fa';
import './GitHubActivity.css';

const GITHUB_USERNAME = 'lakshmananr';

const GitHubActivity = () => {
    const [repos, setRepos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchRepos = async () => {
            try {
                const res = await fetch(
                    `https://api.github.com/users/${GITHUB_USERNAME}/repos?sort=updated&per_page=6`
                );
                if (!res.ok) throw new Error('GitHub API request failed');
                const data = await res.json();
                setRepos(data);
            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };
        fetchRepos();
    }, []);

    const langColor = {
        JavaScript: '#f1e05a',
        Python: '#3572A5',
        HTML: '#e34c26',
        CSS: '#563d7c',
        TypeScript: '#2b7489',
        Java: '#b07219',
    };

    return (
        <section className="github-activity section" id="github">
            <div className="container">
                <motion.div
                    className="section-header"
                    initial={{ opacity: 0, y: 50 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8 }}
                    viewport={{ once: true }}
                >
                    <h2 className="section-title">GitHub Activity</h2>
                    <p className="section-subtitle">Recent public repositories</p>
                </motion.div>

                {/* Contribution graph embed */}
                <motion.div
                    className="github-graph-wrap"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7 }}
                    viewport={{ once: true }}
                >
                    <img
                        src={`https://ghchart.rshah.org/667eea/${GITHUB_USERNAME}`}
                        alt="GitHub contribution graph"
                        className="github-graph"
                    />
                </motion.div>

                {/* Repos grid */}
                {loading ? (
                    <div className="github-loading">
                        <FaSync className="github-spin" />
                        <span>Fetching repositories…</span>
                    </div>
                ) : error ? (
                    <div className="github-error">
                        <FaExclamationCircle />
                        <span>Couldn't load repositories right now.</span>
                    </div>
                ) : (
                    <div className="github-repos">
                        {repos.map((repo, index) => (
                            <motion.a
                                key={repo.id}
                                href={repo.html_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="repo-card"
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.4, delay: index * 0.07 }}
                                viewport={{ once: true }}
                                whileHover={{ y: -4, transition: { duration: 0.2 } }}
                            >
                                <div className="repo-header">
                                    <FaGithub className="repo-icon" />
                                    <span className="repo-name">{repo.name}</span>
                                    {repo.fork && <span className="repo-fork-badge">Fork</span>}
                                </div>

                                <p className="repo-desc">
                                    {repo.description || 'No description provided.'}
                                </p>

                                <div className="repo-footer">
                                    {repo.language && (
                                        <span className="repo-lang">
                                            <span
                                                className="lang-dot"
                                                style={{ background: langColor[repo.language] || '#aaa' }}
                                            />
                                            {repo.language}
                                        </span>
                                    )}
                                    <span className="repo-stat">
                                        <FaStar /> {repo.stargazers_count}
                                    </span>
                                    <span className="repo-stat">
                                        <FaCodeBranch /> {repo.forks_count}
                                    </span>
                                </div>
                            </motion.a>
                        ))}
                    </div>
                )}

                <motion.div
                    className="github-cta"
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    transition={{ duration: 0.8, delay: 0.4 }}
                    viewport={{ once: true }}
                >
                    <a
                        href={`https://github.com/${GITHUB_USERNAME}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn"
                    >
                        <FaGithub /> View GitHub Profile
                    </a>
                </motion.div>
            </div>
        </section>
    );
};

export default GitHubActivity;
